import React, { useEffect } from "react"
import {
  TextField,
  Grid,
  Divider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import "dayjs/locale/es"
import PersonIcon from "@mui/icons-material/Person"
import PhoneIcon from "@mui/icons-material/Phone"
import EmailIcon from "@mui/icons-material/Email"
import LocationIcon from "@mui/icons-material/LocationOn"
import BadgeIcon from "@mui/icons-material/Badge"
import PublicIcon from "@mui/icons-material/Public"
import MarkunreadMailboxIcon from "@mui/icons-material/MarkunreadMailbox"
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"

// Lista de países hardcoded
const PAISES = [
  "Argentina",
  "Bolivia",
  "Brasil",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Cuba",
  "Ecuador",
  "El Salvador",
  "España",
  "Estados Unidos",
  "Guatemala",
  "Honduras",
  "México",
  "Nicaragua",
  "Panamá",
  "Paraguay",
  "Perú",
  "Puerto Rico",
  "República Dominicana",
  "Uruguay",
  "Venezuela",
]

// Función para detectar el país por defecto basado en la zona horaria
const detectarPaisPorDefecto = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    // Mapeo de zonas horarias a países (principales)
    const timezoneToPais = {
      "America/Argentina": "Argentina",
      "America/La_Paz": "Bolivia",
      "America/Sao_Paulo": "Brasil",
      "America/Santiago": "Chile",
      "America/Bogota": "Colombia",
      "America/Costa_Rica": "Costa Rica",
      "America/Havana": "Cuba",
      "America/Guayaquil": "Ecuador",
      "America/El_Salvador": "El Salvador",
      "Europe/Madrid": "España",
      "America/New_York": "Estados Unidos",
      "America/Los_Angeles": "Estados Unidos",
      "America/Chicago": "Estados Unidos",
      "America/Guatemala": "Guatemala",
      "America/Tegucigalpa": "Honduras",
      "America/Mexico_City": "México",
      "America/Managua": "Nicaragua",
      "America/Panama": "Panamá",
      "America/Asuncion": "Paraguay",
      "America/Lima": "Perú",
      "America/Puerto_Rico": "Puerto Rico",
      "America/Santo_Domingo": "República Dominicana",
      "America/Montevideo": "Uruguay",
      "America/Caracas": "Venezuela",
    }
    
    // Buscar coincidencia en el timezone
    for (const [tz, pais] of Object.entries(timezoneToPais)) {
      if (timezone.includes(tz)) {
        return pais
      }
    }
    
    // Por defecto, retornar Argentina (puedes cambiar esto)
    return "Argentina"
  } catch (error) {
    console.error("Error detectando país:", error)
    return "Argentina"
  }
}

const FormularioEntidadComponent = ({
  descripcionEntidad,
  setDescripcionEntidad,
  apellidoEntidad,
  setApellidoEntidad,
  dniCuitCuilEntidad,
  setdniCuitCuilEntidad,
  telefonoEntidad,
  setTelefonoEntidad,
  emailEntidad,
  setEmailEntidad,
  direccionEntidad,
  setDireccionEntidad,
  localidadEntidad,
  setLocalidadEntidad,
  provinciaEntidad,
  setProvinciaEntidad,
  idTipoEntidadEntidad,
  setIdTipoEntidadEntidad,
  idCondicionIvaEntidad,
  setIdCondicionIvaEntidad,
  tipoEntidades,
  condicionIva,
  errors,
  showCondicionIva = true,
  compact = false,
  tipoEntidadReadonly = false,
  tipoEntidadPredeterminado = null,
  // NUEVOS PROPS
  sexoEntidad,
  setSexoEntidad,
  fechaNacimientoEntidad,
  setFechaNacimientoEntidad,
  paisEntidad,
  setPaisEntidad,
  codigoPostalEntidad,
  setCodigoPostalEntidad,
  observacionesEntidad,
  setObservacionesEntidad,
  esTutorEntidad,
  setEsTutorEntidad,
  nombreTutorEntidad,
  setNombreTutorEntidad,
  apellidoTutorEntidad,
  setApellidoTutorEntidad,
  telefonoTutorEntidad,
  setTelefonoTutorEntidad,
  ocultarInformacionContacto = false,
  // DATOS BANCARIOS
  nombreBancoEntidad,
  setNombreBancoEntidad,
  numeroCuentaEntidad,
  setNumeroCuentaEntidad,
  cbuCvuCuentaEntidad,
  setCbuCvuCuentaEntidad,
  aliasCuentaEntidad,
  setAliasCuentaEntidad,
  // ES VENDEDOR
  esVendedorEntidad,
  setEsVendedorEntidad,
  // Validación DNI único proveedores
  tipoEntidad,
  idEntidadActual,
  setErrors,
  existeProveedorConDNI,
  extraContent = null,
}) => {
  // Establecer tipo de entidad predeterminado al montar el componente
  useEffect(() => {
    if (tipoEntidadPredeterminado && !idTipoEntidadEntidad) {
      setIdTipoEntidadEntidad(tipoEntidadPredeterminado)
    }
  }, [tipoEntidadPredeterminado, idTipoEntidadEntidad, setIdTipoEntidadEntidad])

  // Detectar y establecer país por defecto al montar el componente
  useEffect(() => {
    if (!paisEntidad && setPaisEntidad) {
      const paisDetectado = detectarPaisPorDefecto()
      setPaisEntidad(paisDetectado)
    }
  }, []) // Solo se ejecuta una vez al montar

  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: "#5b5b5f",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#5b5b5f",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#5b5b5f",
    },
  }

  return (
    <Grid container spacing={3}>
      {/* Información Personal */}
      <Grid item xs={12}>
        <Typography variant={compact ? "subtitle1" : "h6"} sx={{ color: "#5b5b5f", fontWeight: 600, mb: 0.5, mt: 0 }}>
          Información Personal
        </Typography>
        <Divider sx={{ mb: 1 }} />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Nombre"
          variant="outlined"
          size="small"
          fullWidth
          value={descripcionEntidad}
          onChange={(e) => setDescripcionEntidad(e.target.value)}
          error={!!errors.descripcion}
          helperText={errors.descripcion}
          InputProps={{
            startAdornment: <PersonIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Apellido"
          variant="outlined"
          size="small"
          fullWidth
          value={apellidoEntidad}
          onChange={(e) => setApellidoEntidad(e.target.value)}
          InputProps={{
            startAdornment: <PersonIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="DNI/CUIT/CUIL"
          variant="outlined"
          size="small"
          fullWidth
          value={dniCuitCuilEntidad}
          onChange={(e) => setdniCuitCuilEntidad(e.target.value)}
          onBlur={async () => {
            if (tipoEntidad === "proveedor" && setErrors && existeProveedorConDNI && dniCuitCuilEntidad?.trim()) {
              const existe = await existeProveedorConDNI(dniCuitCuilEntidad.trim(), idEntidadActual)
              setErrors((prev) =>
                existe
                  ? { ...prev, dniCuitCuil: "Ya existe un proveedor con ese DNI/CUIT/CUIL" }
                  : { ...prev, dniCuitCuil: undefined }
              )
            }
          }}
          error={!!errors.dniCuitCuil}
          helperText={errors.dniCuitCuil}
          InputProps={{
            startAdornment: <BadgeIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl
          fullWidth
          size="small"
          error={!!errors.idTipoEntidad}
          sx={fieldStyle}
        >
          <InputLabel>Tipo de Entidad *</InputLabel>
          <Select
            value={
              idTipoEntidadEntidad === "" || idTipoEntidadEntidad == null
                ? ""
                : Number(idTipoEntidadEntidad)
            }
            onChange={(e) => {
              const v = e.target.value
              setIdTipoEntidadEntidad(v === "" ? "" : Number(v))
            }}
            label="Tipo de Entidad *"
            size="small"
            disabled={tipoEntidadReadonly}
            sx={tipoEntidadReadonly ? {
              backgroundColor: "#f5f5f5",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
            } : {}}
          >
            {tipoEntidades.map((tipoEntidad) => (
              <MenuItem key={tipoEntidad.id} value={Number(tipoEntidad.id)}>
                {tipoEntidad.descripcion}
              </MenuItem>
            ))}
          </Select>
          {errors.idTipoEntidad && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {errors.idTipoEntidad}
            </Typography>
          )}
        </FormControl>
      </Grid>

      {showCondicionIva && (
        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            size="small"
            sx={fieldStyle}
          >
            <InputLabel>Condición IVA</InputLabel>
            <Select
              value={idCondicionIvaEntidad}
              onChange={(e) => setIdCondicionIvaEntidad(e.target.value)}
              label="Condición IVA"
              size="small"
            >
              <MenuItem value="">
                <em>Sin seleccionar</em>
              </MenuItem>
              {condicionIva.map((condicion) => (
                <MenuItem key={condicion.id} value={condicion.id}>
                  {condicion.descripcion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {/* Checkbox esVendedor: solo visible para proveedores (idTipoEntidadEntidad === 2) */}
      {idTipoEntidadEntidad === 2 && (
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={esVendedorEntidad || false}
                onChange={(e) => setEsVendedorEntidad && setEsVendedorEntidad(e.target.checked)}
                sx={{
                  color: "#5b5b5f",
                  "&.Mui-checked": { color: "#5b5b5f" },
                }}
              />
            }
            label="Es Vendedor"
            sx={{ color: "#5b5b5f" }}
          />
        </Grid>
      )}

      {/* Ocultar sexo y fecha de nacimiento para proveedores (idTipoEntidadEntidad === 2) */}
      {idTipoEntidadEntidad !== 2 && (
        <>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" sx={fieldStyle}>
              <InputLabel>Sexo</InputLabel>
              <Select
                value={sexoEntidad || ""}
                onChange={(e) => setSexoEntidad(e.target.value)}
                label="Sexo"
                size="small"
              >
                <MenuItem value="">
                  <em>Sin especificar</em>
                </MenuItem>
                <MenuItem value="Masculino">Masculino</MenuItem>
                <MenuItem value="Femenino">Femenino</MenuItem>
                <MenuItem value="X">X</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                label="Fecha de Nacimiento"
                value={fechaNacimientoEntidad}
                onChange={(newValue) => setFechaNacimientoEntidad(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: fieldStyle,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
        </>
      )}

      {/* Información de Contacto */}
      <Grid item xs={12}>
        <Typography variant={compact ? "subtitle1" : "h6"} sx={{ color: "#5b5b5f", fontWeight: 600, mb: 0.5, mt: 1 }}>
          Información de Contacto
        </Typography>
        <Divider sx={{ mb: 1 }} />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Teléfono"
          variant="outlined"
          size="small"
          fullWidth
          value={telefonoEntidad}
          onChange={(e) => setTelefonoEntidad(e.target.value)}
          InputProps={{
            startAdornment: <PhoneIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Email"
          variant="outlined"
          size="small"
          fullWidth
          value={emailEntidad}
          onChange={(e) => setEmailEntidad(e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          InputProps={{
            startAdornment: <EmailIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      {/* Información de Ubicación */}
      <Grid item xs={12}>
        <Typography variant={compact ? "subtitle1" : "h6"} sx={{ color: "#5b5b5f", fontWeight: 600, mb: 0.5, mt: 1 }}>
          Información de Ubicación
        </Typography>
        <Divider sx={{ mb: 1 }} />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Dirección"
          variant="outlined"
          size="small"
          fullWidth
          value={direccionEntidad}
          onChange={(e) => setDireccionEntidad(e.target.value)}
          InputProps={{
            startAdornment: <LocationIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Localidad"
          variant="outlined"
          size="small"
          fullWidth
          value={localidadEntidad}
          onChange={(e) => setLocalidadEntidad(e.target.value)}
          InputProps={{
            startAdornment: <LocationIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Provincia"
          variant="outlined"
          size="small"
          fullWidth
          value={provinciaEntidad}
          onChange={(e) => setProvinciaEntidad(e.target.value)}
          InputProps={{
            startAdornment: <LocationIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Autocomplete
          options={PAISES}
          value={paisEntidad || null}
          onChange={(event, newValue) => setPaisEntidad(newValue || "")}
          freeSolo
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label="País"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <PublicIcon sx={{ color: "#5b5b5f", mr: 1, ml: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={fieldStyle}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Código Postal"
          variant="outlined"
          size="small"
          fullWidth
          value={codigoPostalEntidad}
          onChange={(e) => setCodigoPostalEntidad(e.target.value)}
          InputProps={{
            startAdornment: <MarkunreadMailboxIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Observaciones"
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={observacionesEntidad}
          onChange={(e) => setObservacionesEntidad(e.target.value)}
          placeholder="Información adicional: alergias, si es celíaco, restricciones alimentarias, etc."
          sx={fieldStyle}
        />
      </Grid>

      {/* Información del Contacto */}
      {!ocultarInformacionContacto && (
        <>
          <Grid item xs={12}>
            <Typography variant={compact ? "subtitle1" : "h6"} sx={{ color: "#5b5b5f", fontWeight: 600, mb: 0.5, mt: 1 }}>
              Información del Contacto
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={esTutorEntidad || false}
                  onChange={(e) => setEsTutorEntidad(e.target.checked)}
                  sx={{ color: "#5b5b5f", "&.Mui-checked": { color: "#5b5b5f" } }}
                />
              }
              label="Información de contacto"
            />
          </Grid>
        </>
      )}

      {esTutorEntidad && !ocultarInformacionContacto && (
        <>
          <Grid item xs={12} md={4}>
            <TextField
              label="Nombre del Tutor"
              variant="outlined"
              size="small"
              fullWidth
              value={nombreTutorEntidad}
              onChange={(e) => setNombreTutorEntidad(e.target.value)}
              InputProps={{
                startAdornment: <PersonIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
              }}
              sx={fieldStyle}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Apellido del Tutor"
              variant="outlined"
              size="small"
              fullWidth
              value={apellidoTutorEntidad}
              onChange={(e) => setApellidoTutorEntidad(e.target.value)}
              InputProps={{
                startAdornment: <PersonIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
              }}
              sx={fieldStyle}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Teléfono de Contacto del Tutor"
              variant="outlined"
              size="small"
              fullWidth
              value={telefonoTutorEntidad}
              onChange={(e) => setTelefonoTutorEntidad(e.target.value)}
              InputProps={{
                startAdornment: <PhoneIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
              }}
              sx={fieldStyle}
            />
          </Grid>
        </>
      )}

      {/* Información Bancaria */}
      <Grid item xs={12}>
        <Typography variant={compact ? "subtitle1" : "h6"} sx={{ color: "#5b5b5f", fontWeight: 600, mb: 0.5, mt: 1 }}>
          Datos Bancarios
        </Typography>
        <Divider sx={{ mb: 1 }} />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Nombre del Banco"
          variant="outlined"
          size="small"
          fullWidth
          value={nombreBancoEntidad || ""}
          onChange={(e) => setNombreBancoEntidad(e.target.value)}
          InputProps={{
            startAdornment: <AccountBalanceIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Número de Cuenta"
          variant="outlined"
          size="small"
          fullWidth
          value={numeroCuentaEntidad || ""}
          onChange={(e) => setNumeroCuentaEntidad(e.target.value)}
          InputProps={{
            startAdornment: <AccountBalanceIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="CBU / CVU"
          variant="outlined"
          size="small"
          fullWidth
          value={cbuCvuCuentaEntidad || ""}
          onChange={(e) => setCbuCvuCuentaEntidad(e.target.value)}
          InputProps={{
            startAdornment: <AccountBalanceIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Alias"
          variant="outlined"
          size="small"
          fullWidth
          value={aliasCuentaEntidad || ""}
          onChange={(e) => setAliasCuentaEntidad(e.target.value)}
          InputProps={{
            startAdornment: <AccountBalanceIcon sx={{ color: "#5b5b5f", mr: 1 }} />,
          }}
          sx={fieldStyle}
        />
      </Grid>

      {extraContent && <Grid item xs={12}>{extraContent}</Grid>}
    </Grid>
  )
}

export default FormularioEntidadComponent
